import { useMemo, useRef, useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useFabricCatalog } from "@/contexts/FabricCatalogContext";

/**
 * Searchable lining code input.
 *
 * Behaves like a free-text Input but, while the user types, shows a dropdown of
 * matching lining codes from the catalog (`FABRIC_DATA.linings`). Click a match
 * to insert its rc_code. Falls back to plain free-text entry — the user can
 * still type a custom value the catalog hasn't ingested yet.
 */
export default function LiningCodeSearch({
  value,
  onChange,
  placeholder,
  testId,
}) {
  const { FABRIC_DATA } = useFabricCatalog();
  const linings = FABRIC_DATA?.linings || [];
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const matches = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q) return linings.slice(0, 12);
    return linings
      .filter((l) => {
        const code = (l.rc_code || "").toLowerCase();
        const book = (l.book || "").toLowerCase();
        const color = (l.color || "").toLowerCase();
        return code.includes(q) || book.includes(q) || color.includes(q);
      })
      .slice(0, 20);
  }, [value, linings]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (code) => {
    onChange(code);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Input
          value={value || ""}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="bg-[#0a1628] border-white/10 h-7 text-xs pr-6"
          data-testid={testId}
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
          aria-label="Toggle lining dropdown"
          data-testid={`${testId}-chevron`}
        >
          <ChevronDown size={12} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
        </button>
      </div>
      {open && matches.length > 0 && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 max-h-60 overflow-auto rounded-md border border-white/10 bg-[#0a1628] shadow-lg"
          data-testid={`${testId}-dropdown`}
        >
          {matches.map((m) => (
            <button
              key={m.rc_code}
              type="button"
              onClick={() => pick(m.rc_code)}
              className="w-full text-left px-2 py-1.5 text-[11px] text-slate-200 hover:bg-purple-600/30 flex items-center justify-between gap-2 border-b border-white/5 last:border-0"
              data-testid={`${testId}-opt-${m.rc_code}`}
            >
              <span className="font-mono">{m.rc_code}</span>
              <span className="text-[9px] text-slate-500 truncate">
                {m.book ? `Book ${m.book}` : ""}
                {m.color ? ` · ${m.color}` : ""}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
